// soPartyFill.ts — SUN-283 (lane 3, item 1).
//
// What the Sales Order editor fills when the rep picks an Account or a Contact:
// buyer GSTIN, Buyer State (place of supply), customer type and the bill-to /
// ship-to addresses — from what the record already holds, never a guess.
// A new pick REPLACES what the earlier party filled (no stale GSTIN / state);
// a value the rep typed by hand is kept (`auto` remembers what a pick filled).

import { accountDetails, contactDetails, partyCustomerType } from '@/utils/partyGst';
import { addressFieldsFromJson, type AddressFields } from './soAddressFill';

export interface SoPartyFill {
  accountId?: string;
  buyerGstin: string;
  buyerState: string;
  customerType: 'b2b' | 'b2c';
  billingAddress: AddressFields | null;
  shippingAddress: AddressFields | null;
}

type AccountLike = {
  id?: string;
  gstin?: string | null;
  stateCode?: string | null;
  customerSegment?: string | null;
  billingAddress?: unknown;
  shippingAddress?: unknown;
};

/** An account: its GSTIN, its state (stateCode, else GSTIN prefix), B2B when it has a GSTIN, its addresses. */
export function soFillFromAccount(account: AccountLike | null | undefined): SoPartyFill {
  const d = accountDetails(account);
  return {
    buyerGstin: d.gstin,
    buyerState: d.stateCode,
    customerType: partyCustomerType({ gstin: d.gstin, segment: account?.customerSegment }),
    billingAddress: addressFieldsFromJson(account?.billingAddress),
    shippingAddress: addressFieldsFromJson(account?.shippingAddress),
  };
}

/**
 * A contact: GSTIN = its own, else its account's; state = account stateCode → GSTIN prefix → contact's own state;
 * account = the contact's account; bill-to = the account's billing address, else the contact's address.
 */
export function soFillFromContact(contact: {
  gstin?: string | null; accountId?: string | null; state?: string | null; address?: unknown;
  customerType?: string | null; account?: AccountLike | null;
} | null | undefined): SoPartyFill {
  const d = contactDetails(contact);
  return {
    accountId: d.accountId,
    buyerGstin: d.gstin,
    buyerState: d.stateCode,
    customerType: partyCustomerType({ gstin: d.gstin, segment: contact?.customerType ?? contact?.account?.customerSegment }),
    billingAddress: addressFieldsFromJson(contact?.account?.billingAddress) ?? addressFieldsFromJson(contact?.address),
    shippingAddress: addressFieldsFromJson(contact?.account?.shippingAddress),
  };
}

/** The customer type of a SAVED order (the order has no column for it): B2B when it was saved with a GSTIN, else its party's type. */
export function soCustomerTypeOnOpen(so: { buyerGstin?: string | null; contact?: { customerType?: string | null } | null; account?: { customerSegment?: string | null } | null }): 'b2b' | 'b2c' {
  return partyCustomerType({ gstin: so.buyerGstin, segment: so.contact?.customerType ?? so.account?.customerSegment });
}

const BLANK_ADDRESS = { line1: '', line2: '', city: '', state: '', pincode: '' };

/**
 * Put a pick into the form. GSTIN / state / account: the new party's value replaces; when the new party has none,
 * the field is cleared only if an earlier pick (or the saved order) filled it. Customer type always follows the pick.
 * Addresses: the new party's address replaces the block; when it has none, a block an earlier pick filled is cleared.
 */
export function applySoPartyFill<F extends {
  accountId: string; buyerGstin: string; buyerState: string; customerType: string;
  billingAddress: Record<string, any>; shippingAddress: Record<string, any>;
}>(form: F, fill: SoPartyFill, auto: Set<string>): F {
  const next: any = { ...form, customerType: fill.customerType };
  const flat: Array<'accountId' | 'buyerGstin' | 'buyerState'> = fill.accountId === undefined
    ? ['buyerGstin', 'buyerState']
    : ['accountId', 'buyerGstin', 'buyerState'];
  for (const k of flat) {
    const v = (fill as any)[k] as string | undefined;
    if (v) { next[k] = v; auto.add(k); }
    else if (auto.has(k)) { next[k] = ''; auto.delete(k); }
  }
  for (const k of ['billingAddress', 'shippingAddress'] as const) {
    const a = fill[k];
    if (a) { next[k] = { ...BLANK_ADDRESS, country: form[k]?.country || 'India', ...a }; auto.add(k); }
    else if (auto.has(k)) { next[k] = { ...BLANK_ADDRESS, country: form[k]?.country || 'India' }; auto.delete(k); }
  }
  return next as F;
}
